import React, { useEffect, useState } from 'react';
import {
  Box, CssBaseline, Typography, Avatar, IconButton, Stack, Button, Paper, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, MenuItem, 
  Select, FormControl, InputLabel, Chip, TextField, InputAdornment, Grid, 
  Tooltip, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, 
  DialogContent, DialogActions, Breadcrumbs, Link, Backdrop
} from '@mui/material';
import {
  School, Search, ArrowBack, PersonAddAlt1, Save, WarningAmber, FilterList
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const colors = {
  pageBg: '#F8F9FA',
  sidebarBg: '#F1F2F6',
  filterBg: "#FFF9E6",
  accent: '#FFC107',
  tableHeader: '#FDF7E2',
  textMain: '#5D4037',
  examBottom: '#FFEBEE', // Red
  examMiddle: '#FFF9C4', // Yellow
  examTop: '#E8F5E9',    // Green
};

const classOptions = [
  { id: "D1", title: "Darjah 1" }, { id: "D2", title: "Darjah 2" },
  { id: "D3", title: "Darjah 3" }, { id: "D4", title: "Darjah 4" },
  { id: "D5", title: "Darjah 5" }, { id: "D6", title: "Darjah 6" },
  { id: "F1", title: "Tingkatan 1" }, { id: "F2", title: "Tingkatan 2" },
  { id: "F3", title: "Tingkatan 3" }, { id: "F4", title: "Tingkatan 4" },
  { id: "F5", title: "Tingkatan 5" }
];

const monthList = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const yearList = Array.from({ length: 5 }, (_, i) => (moment().year() - i).toString());

export default function ExamTemp() {
  const navigate = useNavigate();
  
  // --- Logic State ---
  const [filtered, setFiltered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [classroom, setClassroom] = useState('NA');
  const [month, setMonth] = useState(moment().format("MMMM"));
  const [year, setYear] = useState(moment().format("YYYY"));
  const [studentData, setStudentData] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [openAdd, setOpenAdd] = useState(false);
  const [tempName, setTempName] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, msg: "", sev: "success" });

  // --- Dirty State & Custom Prompt State ---
  const [isDirty, setIsDirty] = useState(false);
  const [openBackPrompt, setOpenBackPrompt] = useState(false);

  useEffect(() => {
    localStorage.setItem("current_tab", "Exam");
  }, []);

  // --- API Logic ---
  const getStudentData = (type) => {
    setLoading(true);
    fetch(`https://www.geniusmileniaeducation.link/api/getStudentAllExam/${classroom}(${year})`)
      .then((res) => res.json())
      .then((json) => {
        if (json.data) {
          if (type === "get") {
            setStudentData(json.data);
            setIsDirty(false);
          }
          if (type === "set") submitStudentData();
        } else {
          setStudentData([]);
        }
        setLoading(false);
      }).catch(() => setLoading(false));
  };

  const submitStudentData = () => {
    setSubmitting(true);
    fetch(`https://www.geniusmileniaeducation.link/api/setStudentAllExam/${classroom}(${year})`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(studentData)
    }).then((res) => res.json())
      .then(() => {
        setSubmitting(false);
        setIsDirty(false); 
        setSnackbar({ open: true, msg: "Exam data saved successfully", sev: "success" });
      }).catch(() => {
        setSubmitting(false);
        setSnackbar({ open: true, msg: "Failed to save data", sev: "error" });
      });
  };

  const kPrefix = `K${monthList.indexOf(month) + 1}`;

  const getDynamicColumns = () => {
    const common = [{ id: 'Student_Name', name: 'Nama Student' }];
    const subjects = [];
    if (classroom.startsWith('D')) subjects.push('Bahasa_Melayu', 'Bahasa_Inggeris', 'Matematik', 'Sains');
    else if (['F1', 'F2', 'F3'].includes(classroom)) subjects.push('Bahasa_Melayu', 'Bahasa_Inggeris', 'Matematik', 'Sains', 'Sejarah');
    else subjects.push('Bahasa_Melayu', 'Bahasa_Inggeris', 'Matematik', 'Sains', 'Sejarah', 'Akaun', 'Add_Math', 'Biologi', 'Kimia', 'Fizik', 'Perniagaan', 'Ekonomi');
    
    return [...common, ...subjects.map(sub => ({ id: `${kPrefix}_${sub}`, name: sub.replace(/_/g, ' ') }))];
  };

  const getCellStyles = (val) => {
    if (!val || val === "") return { bgcolor: 'transparent' };
    const n = parseInt(val);
    if (isNaN(n)) return { bgcolor: '#F5F5F5', color: '#757575' }; 
    if (n < 40) return { bgcolor: colors.examBottom, color: '#C62828' };
    const threshold = (classroom.startsWith('F4') || classroom.startsWith('F5')) ? 70 : 80;
    return n < threshold ? { bgcolor: colors.examMiddle, color: '#827717' } : { bgcolor: colors.examTop, color: '#2E7D32' };
  };

  const onChangeInput = (sname, colId, value) => {
    setIsDirty(true);
    setStudentData(prev => prev.map(s => s.Student_Name === sname ? { ...s, [colId]: value } : s));
  };

  const handleRequestBack = () => {
    if (isDirty) setOpenBackPrompt(true);
    else { setFiltered(false); setStudentData([]); }
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
      <CssBaseline />
      
      <Backdrop sx={{ color: colors.accent, zIndex: (theme) => theme.zIndex.drawer + 10 }} open={loading}>
        <Stack alignItems="center" spacing={2}>
            <CircularProgress color="inherit" />
            <Typography variant="h6" color="white" sx={{ fontWeight: 'bold' }}>Loading Exam Records...</Typography>
        </Stack>
      </Backdrop>

      <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>
        
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
          <Box>
            <Breadcrumbs sx={{ mb: 1 }}>
                <Link underline="hover" color="inherit" onClick={() => navigate("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                <Typography color="text.primary">Exam Results</Typography>
            </Breadcrumbs>
            <Typography variant="h4" fontWeight="bold" color={colors.textMain}>
                {filtered ? `${classroom} ${month} Session` : 'Exam Results'}
            </Typography>
          </Box>
          {filtered && (
            <Stack direction="row" spacing={2}>
                <Button variant="outlined" startIcon={<ArrowBack />} onClick={handleRequestBack} sx={{ borderRadius: 2 }}>Change Session</Button>
                <Button variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Save />} onClick={() => getStudentData("set")} 
                    sx={{ bgcolor: isDirty ? colors.accent : 'black', color: isDirty ? 'black' : 'white', borderRadius: 2, px: 4, '&:hover': { bgcolor: isDirty ? '#e6ae06' : '#333' } }}>
                    {isDirty ? "Save Marks *" : "All Saved"}
                </Button>
            </Stack>
          )}
        </Stack>

        {!filtered ? (
          /* --- SESSION SELECTION UI --- */
          <Paper elevation={0} sx={{ p: 4, bgcolor: colors.filterBg, borderRadius: 4, textAlign: 'center', border: '1px solid #F0E6CC' }}>
            <School sx={{ fontSize: 60, color: colors.accent, mb: 2 }} />
            <Typography variant="h5" fontWeight="bold" mb={3} color={colors.textMain}>Select Exam Details</Typography>
            <Grid container spacing={2} justifyContent="center">
              <Grid item xs={12} sm={3}><FormControl fullWidth size="small"><InputLabel>Class</InputLabel><Select value={classroom} label="Class" onChange={(e) => setClassroom(e.target.value)} sx={{ bgcolor: 'white' }}>{classOptions.map(c => <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>)}</Select></FormControl></Grid>
              <Grid item xs={12} sm={3}><FormControl fullWidth size="small"><InputLabel>Month</InputLabel><Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)} sx={{ bgcolor: 'white' }}>{monthList.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}</Select></FormControl></Grid>
              <Grid item xs={12} sm={3}><FormControl fullWidth size="small"><InputLabel>Year</InputLabel><Select value={year} label="Year" onChange={(e) => setYear(e.target.value)} sx={{ bgcolor: 'white' }}>{yearList.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}</Select></FormControl></Grid>
            </Grid>
            <Button variant="contained" onClick={() => classroom !== 'NA' ? [setFiltered(true), getStudentData("get")] : alert("Please pick a class")} sx={{ mt: 4, bgcolor: colors.accent, color: 'black', fontWeight: 'bold', px: 6, py: 1.5, borderRadius: 3 }}>
                Search
            </Button>
          </Paper>
        ) : (
          /* --- DATA GRID UI --- */
          <>
            <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: colors.filterBg, borderRadius: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <FilterList sx={{ color: colors.textMain }} />
              <TextField size="small" placeholder="Search Student Name..." fullWidth onChange={(e) => setFilterSearch(e.target.value.toUpperCase())} sx={{ bgcolor: 'white', borderRadius: 2 }} InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
              <Tooltip title="Add New Student">
                <IconButton onClick={() => setOpenAdd(true)} sx={{ bgcolor: colors.accent, color: 'white', '&:hover': { bgcolor: colors.textMain } }}>
                    <PersonAddAlt1 />
                </IconButton>
              </Tooltip>
            </Paper>

            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #F0E6CC', borderRadius: 4, maxHeight: 600 }}>
              <Table stickyHeader sx={{ borderCollapse: 'separate' }}>
                <TableHead>
                  <TableRow>
                    {getDynamicColumns().map((col) => (
                      <TableCell 
                        key={col.id} 
                        sx={{ 
                          bgcolor: col.id === 'Student_Name' ? colors.accent : colors.tableHeader, 
                          fontWeight: 'bold', 
                          zIndex: col.id === 'Student_Name' ? 11 : 10, 
                          left: col.id === 'Student_Name' ? 0 : 'auto', 
                          position: col.id === 'Student_Name' ? 'sticky' : 'static', 
                          borderRight: col.id === 'Student_Name' ? '2px solid #E0D6B7' : '1px solid #FFF' 
                        }}
                      >
                        {col.name}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentData.filter(s => s.Student_Name?.includes(filterSearch)).map((s, i) => (
                    <TableRow key={i} hover>
                      {getDynamicColumns().map((col) => {
                        const isName = col.id === 'Student_Name';
                        const val = s[col.id] || "";
                        const cellStyle = !isName ? getCellStyles(val) : {};
                        
                        return (
                          <TableCell 
                            key={col.id} 
                            sx={{ 
                                p: isName ? 1 : 0, 
                                position: isName ? 'sticky' : 'static', 
                                left: isName ? 0 : 'auto', 
                                bgcolor: isName ? 'white' : cellStyle.bgcolor, 
                                zIndex: isName ? 5 : 1, 
                                borderRight: isName ? '2px solid #F0E6CC' : '1px solid #FFF' 
                            }}
                          >
                            {isName ? (
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <Avatar sx={{ bgcolor: '#FFECB3', color: '#FF8F00', fontSize: 11, width: 28, height: 28 }}>{s.Student_Name?.substring(0, 2)}</Avatar>
                                    <Typography variant="caption" fontWeight="bold">{s.Student_Name}</Typography>
                                </Stack>
                            ) : (
                                <TextField value={val} onChange={(e) => onChangeInput(s.Student_Name, col.id, e.target.value)} variant="standard" fullWidth 
                                    InputProps={{ disableUnderline: true, sx: { textAlign: 'center', fontSize: '0.85rem', fontWeight: 'bold', color: cellStyle.color, height: '45px', px: 1 } }} 
                                    inputProps={{ style: { textAlign: 'center' } }} 
                                />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Stack direction="row" spacing={2} mt={3} justifyContent="center">
              <Chip label="Critical (< 40)" sx={{ bgcolor: colors.examBottom, color: '#C62828', fontWeight: 'bold' }} />
              <Chip label="Satisfactory" sx={{ bgcolor: colors.examMiddle, color: '#827717', fontWeight: 'bold' }} />
              <Chip label="Excellent" sx={{ bgcolor: colors.examTop, color: '#2E7D32', fontWeight: 'bold' }} />
            </Stack>
          </>
        )}
      </Box>

      {/* --- DISCARD CHANGES PROMPT --- */}
      <Dialog open={openBackPrompt} onClose={() => setOpenBackPrompt(false)} PaperProps={{ sx: { borderRadius: 4, width: 350 } }}>
          <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}><WarningAmber color="warning" /> Unsaved Changes</DialogTitle>
          <DialogContent><Typography variant="body2">You have modified marks that are not saved. Going back will lose these changes. Proceed?</Typography></DialogContent>
          <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setOpenBackPrompt(false)} color="inherit">Stay</Button>
              <Button onClick={() => { setFiltered(false); setIsDirty(false); setStudentData([]); setOpenBackPrompt(false); }} variant="contained" sx={{ bgcolor: colors.errorText, color: 'white' }}>Discard</Button>
          </DialogActions>
      </Dialog>

      {/* --- ADD STUDENT DIALOG --- */}
      <Dialog open={openAdd} onClose={() => setOpenAdd(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Add New Student</DialogTitle>
        <DialogContent><TextField fullWidth autoFocus label="Full Name" sx={{ mt: 2 }} value={tempName} onChange={(e) => setTempName(e.target.value)} /></DialogContent>
        <DialogActions sx={{ p: 2 }}><Button onClick={() => setOpenAdd(false)} color="inherit">Cancel</Button><Button variant="contained" onClick={() => { setStudentData([...studentData, { Student_Name: tempName.toUpperCase() }]); setIsDirty(true); setOpenAdd(false); setTempName(''); }} sx={{ bgcolor: colors.accent, color: 'black', fontWeight: 'bold' }}>Add to List</Button></DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}><Alert severity={snackbar.sev} variant="filled">{snackbar.msg}</Alert></Snackbar>
    </Box>
  );
}